import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';

import { useNavigation } from '@react-navigation/native';

import SubtractIcon from '@/assets/icons/subtract';
import { LineSeparator } from '@/components/LineSeparator';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useNonWithdrawableVerificationCheck } from '@/feature/account-details/hooks/use-non-withdrawable-verification-check';
import { Banner } from '@/feature/betslip-sbk/components/Banner';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { useFantasyMoneyFlags } from '@/hooks/payments/use-fantasy-money-flags';
import { usePaymentMethodTypeEnabled } from '@/hooks/payments/use-payment-method-type-enabled';
import { useSocialWalletTranslation } from '@/hooks/use-social-wallet-translation';
import { WithdrawStackNavigationProp } from '@/navigation/withdraw/types';
import { common } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';

import { useSelectedWithdrawalMethodStore } from '../hooks/use-selected-withdrawal-method';
import { WithdrawMethod } from '../hooks/use-withdrawal-methods';
import { styles as chooseWithdrawalMethodStyles } from '../screens/ChooseWithdrawalMethod';
import { filterMethodsByRequiredDistribution } from '../utils/utils';
import { RedeemToFantasy } from './RedeemToFantasy';
import { WithdrawalMethodItem } from './WithdrawalMethodItem';

type WithdrawalMethodsProps = {
    methods: WithdrawMethod[];
    isPromptedFromForm?: boolean;
};

const styles = StyleSheet.create({
    container: {
        height: 435,
    },
});

/**
 * Component to display withdrawal methods.
 *
 * @param {Array} props.methods - The list of withdrawal methods.
 * @param {boolean} props.isPromptedFromForm - Flag indicating if the component is prompted from a quick withdrawal form.
 */
const WithdrawalMethodsContent = ({ methods, isPromptedFromForm }: WithdrawalMethodsProps) => {
    const tSocialWallet = useSocialWalletTranslation('wallet');
    const { isFantasyMoneyRedemptionEnabled } = useFantasyMoneyFlags();
    const isPaymentMethodTypeEnabled = usePaymentMethodTypeEnabled();
    const navigation = useNavigation<WithdrawStackNavigationProp>();
    const setSelectedMethod = useSelectedWithdrawalMethodStore(state => state.setSelectedMethod);
    const clearSelection = useSelectedWithdrawalMethodStore(state => state.clearSelection);
    const { isVerifiedWithRestrictedBalance } = useNonWithdrawableVerificationCheck();

    // Clear any stale selection when the choose-method list is (re)opened, but keep the
    // current selection highlighted when the sheet is opened from the withdrawal form
    useEffect(() => {
        if (!isPromptedFromForm) {
            clearSelection();
        }
    }, [clearSelection, isPromptedFromForm]);

    // Filter out methods that aren't saved and apply payment method flags
    const savedMethods = methods
        .filter(method => !method.via_site_id)
        .filter(method => isPaymentMethodTypeEnabled(method.type));

    // Hide methods with no required distribution unless the user is verified / light SDD
    // show all methods if no methods have required distribution
    const enabledMethods = filterMethodsByRequiredDistribution(savedMethods, !!isVerifiedWithRestrictedBalance);

    // When a single method remains it should be picked right away
    const singleMethod =
        !isPromptedFromForm && !isFantasyMoneyRedemptionEnabled && enabledMethods.length === 1
            ? enabledMethods[0]
            : undefined;

    useEffect(() => {
        if (!singleMethod) {
            return;
        }
        setSelectedMethod(singleMethod);
        navigation.replace(singleMethod.is_expired ? 'WithdrawalExpiryUpdate' : 'WithdrawForm', {
            selectedMethod: singleMethod,
            methods,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [singleMethod?.payment_type_id]);

    return (
        <>
            {!isPromptedFromForm ? (
                <>
                    <SizedBox value={26} />
                    <Text fontWeight="600" fontSize={22}>
                        {tSocialWallet('choose_withdrawal_method')}
                    </Text>
                    <SizedBox value={28} />
                </>
            ) : null}

            {enabledMethods?.map((method: WithdrawMethod, idx: number) => {
                const isFirstSavedMethod = idx === 0;
                const isLastSavedMethod = idx === enabledMethods.length - 1;
                const shouldShowTopSeparator = !isFirstSavedMethod;
                const shouldShowBottomSeparator = isLastSavedMethod && isFantasyMoneyRedemptionEnabled;

                return (
                    <View key={`${method.name}_${idx}`} style={common.full}>
                        {shouldShowTopSeparator ? (
                            <LineSeparator style={chooseWithdrawalMethodStyles.separator} />
                        ) : null}
                        <WithdrawalMethodItem method={method} methods={methods} />
                        {shouldShowBottomSeparator ? (
                            <LineSeparator style={chooseWithdrawalMethodStyles.separator} />
                        ) : null}
                    </View>
                );
            })}

            {isFantasyMoneyRedemptionEnabled ? <RedeemToFantasy /> : null}

            <SizedBox value={24} />
            <Banner
                leftIcon={<SubtractIcon />}
                backgroundColor={ds.colorToken.background.neutral.subtle}
                message={tSocialWallet('withdrawal_choose_method_banner')}
            />
            <SizedBox value={16} />
        </>
    );
};

export const WithdrawalMethods = ({ methods, isPromptedFromForm = false }: WithdrawalMethodsProps) => {
    const content = (
        <View style={[isPromptedFromForm && styles.container, common.flex]}>
            <ScrollView
                bounces={isPromptedFromForm}
                contentContainerStyle={[common.alignCenter, common.paddingHorizontal]}
            >
                <WithdrawalMethodsContent methods={methods} isPromptedFromForm={isPromptedFromForm} />
            </ScrollView>
        </View>
    );

    if (!isPromptedFromForm && isWeb) {
        return <MaxWidthWrapper flex={1}>{content}</MaxWidthWrapper>;
    }

    return content;
};
